import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, ComposedChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import MapPlaceholder from "../components/ui/MapPlaceholder";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, STATUS_KEY, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveMissionSection } from "../components/live/LiveGovernanceSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  alertFeed, departmentStatus, routeSaturation, crowdTrendData,
  activeIncidents, responseTimeline, resourceDeployment,
  commandTimeline, agencyCoordination, officerLocations, crowdFlowByZone,
  topStats, warRoomTasks, warRoomSITREP, auditLogEntries
} from "../data/mockData";
import {
  Clock, RefreshCw, AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  Users, Zap, Shield, Radio, MapPin, TrendingUp, TrendingDown, Activity,
  User, ChevronRight, FileText, Target, ArrowRight, Building2, Calendar,
  Star, Lock, GitBranch
} from "lucide-react";

// ── incident helpers (from Unified Command) ────────────────────────
// NB: "priority-critical/-high/-medium" classes referenced by the previous
// version of priorityClass had no matching CSS anywhere in the app (dead
// classNames — the badge rendered unstyled). Rebuilt on statusClasses so it
// actually renders a colored pill, consistent with every other badge here.
const priorityBucket = p => p === "CRITICAL" ? "critical" : p === "HIGH" ? "serious" : "warning";
const priorityClass = p => `text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(priorityBucket(p))}`;

const statusTextBucket = s =>
  s === "Escalated" || s === "Active" ? "critical" :
  s === "Responding"                  ? "serious"  :
  s === "In Progress"                 ? "warning"  :
  s === "Monitoring"                  ? "info"     :
  s === "Responded"                   ? "good"     : "neutral";
const STATUS_TEXT_WEIGHT = { critical:"font-bold", serious:"font-bold", warning:"font-semibold", info:"font-semibold", good:"font-semibold", neutral:"" };
const statusTextColor = s => `${STATUS[statusTextBucket(s)].text} ${STATUS_TEXT_WEIGHT[statusTextBucket(s)]}`;

const riskBucket = r => r === "critical" ? "critical" : r === "high" ? "serious" : r === "moderate" ? "warning" : "good";
const riskBg = r => statusClasses(riskBucket(r));

// Timeline event type -> icon + shared status bucket (mirrors
// DashboardHome's timelineIconStyle). "missing"/"ai"/"advisory" have no
// matching STATUS bucket so they keep local violet/indigo/sky overrides.
const TIMELINE_ICON = {
  escalation: { bg: STATUS.critical.bg, text: STATUS.critical.text, Icon: AlertTriangle },
  alert:      { bg: STATUS.serious.bg,  text: STATUS.serious.text,  Icon: Zap },
  action:     { bg: STATUS.info.bg,     text: STATUS.info.text,     Icon: Shield },
  missing:    { bg: "bg-violet-100",    text: "text-violet-600",    Icon: Users },
  ai:         { bg: "bg-indigo-100",    text: "text-indigo-600",    Icon: Activity },
  resolved:   { bg: STATUS.good.bg,     text: STATUS.good.text,     Icon: CheckCircle },
  advisory:   { bg: "bg-sky-100",       text: "text-sky-600",       Icon: Radio },
};
const timelineIcon = t => {
  const cfg = TIMELINE_ICON[t] || { bg:"bg-slate-100", text:"text-slate-600", Icon: Clock };
  return { cls: `${cfg.bg} ${cfg.text}`, Icon: cfg.Icon };
};

const slaUrgency = eta =>
  eta === "Done" ? STATUS.good.text :
  ["14:44","14:50"].includes(eta) ? `${STATUS.critical.text} font-black animate-pulse` : `${STATUS.warning.text} font-bold`;

const INCIDENT_DETAIL = {
  "INC-8821": {
    description:"Crowd density exceeded 9.2 persons/m² at Ramkund Ghat Entry 2. AI Crowd Engine flagged risk at 09:1 level. Police mobilised for crowd diversion to Entry 4.",
    units:["Police Beat Squad B12","SRP Company 3","Crowd Control Van CC-04"],
    actions:[
      { time:"14:33", by:"DCP Nashik",  act:"Approved crowd diversion to Entry 4" },
      { time:"14:28", by:"UNIT-P22",  act:"Barricading activated — Entry 2 restricted" },
      { time:"14:22", by:"AI System",   act:"Density alert escalated — confidence 91%" },
      { time:"14:15", by:"Beat Squad",  act:"Megaphone announcement for crowd movement" },
    ],
  },
  "INC-8820": {
    description:"Water tanker TK-08 failed to arrive at Panchvati Water Point 8. Supply exhausted. NMC logistics contacted for emergency replacement tanker.",
    units:["NMC Tanker Dispatch","Municipal Control Room"],
    actions:[
      { time:"14:28", by:"UNIT-M43",  act:"Logged tanker delay — ETA 15:15" },
      { time:"14:20", by:"NMC Control", act:"Replacement tanker TK-12 dispatched" },
      { time:"14:10", by:"AI System",   act:"Auto-alert raised — 91% capacity breach" },
    ],
  },
  "INC-8819": {
    description:"Ambulance AM-03 access route blocked by crowd at Camp Beta, Trimbak Road. Patient with heatstroke awaiting evacuation. Police escort requested.",
    units:["Ambulance AM-03","Police Escort PQ-01","NDRF Unit 2"],
    actions:[
      { time:"14:42", by:"SP Nashik",   act:"Police escort authorised — route cleared" },
      { time:"14:38", by:"UNIT-H05",act:"Escalated to Health Command for priority clearance" },
      { time:"14:30", by:"AI System",   act:"Route blockage detected — 3 alternate routes suggested" },
    ],
  },
  "INC-8818": {
    description:"Nashik Road Camp 3 shelter at 94% capacity. 1,840 persons sheltered. Revenue Dept requested to open overflow Camp Delta for additional 500 persons.",
    units:["Revenue Dept Team","Camp Management"],
    actions:[
      { time:"14:18", by:"Revenue Dept",act:"Camp Delta activation in progress — ETA 16:00" },
      { time:"14:05", by:"JE Shelters", act:"Overload logged — 94% threshold crossed" },
    ],
  },
  "INC-8817": {
    description:"8-year-old child separated from parents at Panchvati Sector 7. Description registered. AI facial recognition search initiated (with consent). Police teams deployed.",
    units:["Police Search Team PT-02","MP Helpdesk"],
    actions:[
      { time:"14:18", by:"UNIT-P16",act:"Case MP-1185 registered — search ongoing" },
      { time:"14:12", by:"AI System",   act:"CCTV facial recognition search initiated (DPDP consent obtained)" },
    ],
  },
  "INC-8816": {
    description:"Cluster of 6 heatstroke cases reported in Godavari Zone D. Medical team dispatched. All patients stabilised and shifted to Camp Alpha.",
    units:["Medical Team MT-04","Ambulance AM-01"],
    actions:[
      { time:"14:16", by:"UNIT-H05",act:"All 6 patients stabilised — incident resolved" },
      { time:"14:00", by:"Medical Team",act:"Emergency response dispatched — Zone D" },
      { time:"13:58", by:"AI System",   act:"Heatstroke cluster pattern detected — confidence 88%" },
    ],
  },
};

// ── war room helpers (from War Room) ────────────────────────────────
const DECISION_QUEUE = [
  { id:"DEC-088", title:"Seal Ramkund Entry 2 — crowd density 9.2/m²",      reqBy:"AI System",         reqDept:"Maha Kumbh AI",      authority:"DCP Nashik",    deadline:"14:50", aiConf:92, impact:"HIGH",   type:"Crowd Control"   },
  { id:"DEC-087", title:"Redirect tanker TK-18 from Gangapur to Panchvati", reqBy:"NMC JE R.Patil",    reqDept:"Municipal",  authority:"Exec. Eng.",    deadline:"14:48", aiConf:88, impact:"HIGH",   type:"Water Supply"    },
  { id:"DEC-086", title:"Activate Camp Delta overflow — shelter 138%",       reqBy:"Revenue Officer",   reqDept:"Revenue",    authority:"Dist. Collector",deadline:"14:55",aiConf:84, impact:"MED",    type:"Shelter"         },
  { id:"DEC-085", title:"Pre-position NDRF Platoon 1 at Ramkund",           reqBy:"NDRF Commander",    reqDept:"Disaster",   authority:"CM EOC",        deadline:"15:00", aiConf:79, impact:"MED",    type:"Preparedness"    },
  { id:"DEC-084", title:"Issue public advisory — carry water, temp 41°C",   reqBy:"CMO Nashik",        reqDept:"Health",     authority:"DM Office",     deadline:"14:45", aiConf:97, impact:"LOW",    type:"Public Advisory" },
];

const CROSS_AGENCY_REQUESTS = [
  { id:"CAR-44", from:"Health Dept",    to:"Police Dept",    request:"Police escort for AMB-07 — Trimbak Rd",        priority:"CRITICAL",status:"In Progress",time:"14:29" },
  { id:"CAR-43", from:"NMC Municipal",  to:"Revenue Dept",   request:"Open overflow shelter OFZ-B — Camp 3 full",    priority:"HIGH",   status:"Pending",   time:"14:22" },
  { id:"CAR-42", from:"NDRF",           to:"Transport",      request:"Bus BR-09 for 800 displaced pilgrims",         priority:"HIGH",   status:"Approved",  time:"14:15" },
  { id:"CAR-41", from:"Police Dept",    to:"Maha Kumbh AI",       request:"CCTV scan — missing child Gate 5 area",        priority:"HIGH",   status:"Active",    time:"14:12" },
  { id:"CAR-40", from:"Health Dept",    to:"Municipal NMC",  request:"Water supply for Camp Alpha — tanker needed",  priority:"MED",    status:"In Progress",time:"14:08"},
  { id:"CAR-39", from:"Transport",      to:"Police Dept",    request:"Traffic signal override — Godavari Bridge",    priority:"MED",    status:"Approved",  time:"14:02" },
];

const SHAHI_SNAN_PLAN = [
  {
    date:"11 Jul 2027", name:"Shravan Purnima",  daysAway:32,
    crowdEst:"9,00,000", readiness:78, threatLevel:"HIGH",
    pending:["NDRF pre-position (D-7)","Extra bus depot activation","Additional medical camps (3)","Godavari Bridge traffic plan","Volunteer training (D-14)"],
    done:["CM communication protocol approved","Hospital surge plan activated","SITREP template ready","Cross-agency MOU signed"],
  },
  {
    date:"12 Aug 2027", name:"Simhastha Peak Day", daysAway:64,
    crowdEst:"12,00,000",readiness:62, threatLevel:"CRITICAL",
    pending:["Mass evacuation route finalization","All-agency simulation drill","Satellite comms backup","20 additional ambulances","Media embargo protocol"],
    done:["Crowd risk model calibrated","Digital twin scenario ready","Mutual aid agreements signed"],
  },
  {
    date:"14 Sep 2027", name:"Anant Chaturdashi", daysAway:97,
    crowdEst:"8,00,000", readiness:54, threatLevel:"HIGH",
    pending:["Post-peak medical surge plan","Transport exit strategy","Waste management scaling"],
    done:["Base camp allocation complete","Police rotation plan signed"],
  },
];

const WAR_ROOM_LOG = [
  { time:"14:33", actor:"DCP Nashik",        dept:"Police",    msg:"Approved crowd diversion plan — Entry 2 sealed, Gate 4 activated",      type:"decision"  },
  { time:"14:31", actor:"Maha Kumbh AI",          dept:"System",    msg:"Crowd density 9.2/m² alert generated — Ramkund Entry 2",               type:"ai"        },
  { time:"14:29", actor:"CMO Nashik",        dept:"Health",    msg:"Ambulance escort request raised to Police — INC-8819",                  type:"escalate"  },
  { time:"14:26", actor:"Dist. Collector",   dept:"Admin",     msg:"Camp 3 overflow shelter OFZ-B pre-authorized — pending activation",     type:"decision"  },
  { time:"14:22", actor:"MSRTC Nashik",      dept:"Transport", msg:"Shuttle SR-04 diversion confirmed — alternate route activated",         type:"action"    },
  { time:"14:18", actor:"UNIT-M43",dept:"Municipal", msg:"Water tanker TK-22 dispatched — Panchvati Station 8 ETA 14:48",        type:"action"    },
  { time:"14:15", actor:"NDRF Commander",    dept:"Disaster",  msg:"Platoon 2 activated for Camp 3 shelter response",                       type:"action"    },
  { time:"14:12", actor:"Maha Kumbh AI",          dept:"System",    msg:"Missing person CCTV match at Gate 5 — 61% confidence — sent to Patil", type:"ai"        },
  { time:"14:08", actor:"UNIT-H05",   dept:"Health",    msg:"Ambulance access blocked — escalated to DCP for escort priority",       type:"escalate"  },
  { time:"14:02", actor:"SP Nashik",         dept:"Police",    msg:"CM convoy route POL-002 approved — 15:30 ingress",                     type:"decision"  },
  { time:"13:58", actor:"Revenue DM",        dept:"Revenue",   msg:"Camp 3 load at 138% — alert raised to Collector",                      type:"escalate"  },
  { time:"13:44", actor:"UNIT-P16",      dept:"Police",    msg:"MP-1185 registered — broadcast to 22,000 pilgrims activated",          type:"action"    },
];

const RESPONSIBILITY_MATRIX = [
  { domain:"Crowd Management",      primary:"Police",    secondary:"NDRF",      trigger:"Density >8/m²",          escalation:"DCP → Collector" },
  { domain:"Medical Emergencies",   primary:"Health",    secondary:"Police",    trigger:"Serious case / Amb block",escalation:"CMO → DCP → Civil Hosp" },
  { domain:"Water & Sanitation",    primary:"Municipal", secondary:"Revenue",   trigger:"Station empty / overflow",escalation:"JE → Exec.Eng. → Collector"},
  { domain:"Transport & Traffic",   primary:"Transport", secondary:"Police",    trigger:"Route saturation >85%",   escalation:"MSRTC → RTO → DCP" },
  { domain:"Shelter Overflow",      primary:"Revenue",   secondary:"Municipal", trigger:"Camp >120% capacity",     escalation:"RO → Collector → CM" },
  { domain:"Disaster Response",     primary:"NDRF",      secondary:"Police",    trigger:"Flood / Fire / Structure",escalation:"NDRF → DM → CM EOC" },
  { domain:"Missing Person",        primary:"Police",    secondary:"Volunteers",trigger:"MP case registered",      escalation:"PSI → Inspector → DCP" },
  { domain:"Public Communication",  primary:"CM Office", secondary:"All Depts", trigger:"Misinformation / alert",  escalation:"PRO → DM → CM" },
];

const COMMAND_AUTHORITY = [
  { level:"L1 — Field Officer",    holder:"1,120 field staff",  scope:"Execute tasks, update status, flag alerts",         twoFA:false, color:"text-slate-600",    bg:"bg-slate-50"    },
  { level:"L2 — Inspector / JE",   holder:"60 section officers",scope:"Approve L1 actions, assign resources, escalate",   twoFA:true,  color:"text-blue-700",     bg:"bg-blue-50"     },
  { level:"L2 — Dept Head / DCP",  holder:"12 dept heads",      scope:"Approve major ops, inter-agency, resource calls",   twoFA:true,  color:"text-amber-700",    bg:"bg-amber-50"    },
  { level:"L3 — District Collector",holder:"1 Collector",       scope:"Approve cross-agency escalations, CM escalation",  twoFA:true,  color:"text-orange-700",   bg:"bg-orange-50"   },
  { level:"L4 — CM Office / EOC",  holder:"CM + 1 ADC",         scope:"Final authority — national-level response & media", twoFA:true,  color:"text-red-700",      bg:"bg-red-50"      },
];

const VIP_MOVEMENT = [
  { vip:"Chief Minister Nashik Visit",      eta:"15:30", route:"Nashik Road → Ramkund Ghat (secured)", escort:"SP Nashik + 2 Platoons", status:"Approved", security:"L3" },
  { vip:"Union Minister — Trimbakeshwar",   eta:"16:00", route:"Trimbak Road (VIP lane)",               escort:"DySP + 1 Platoon",      status:"Confirmed",security:"L2" },
  { vip:"District Collector — Camp Inspect",eta:"14:55", route:"Camp Alpha → Camp Beta",                escort:"PSI + 4 Constables",    status:"Active",   security:"L1" },
];

const TASK_TREND = [
  { hour:"06:00", created:4,  completed:3,  breached:0 },
  { hour:"07:00", created:8,  completed:7,  breached:0 },
  { hour:"08:00", created:14, completed:12, breached:1 },
  { hour:"09:00", created:22, completed:18, breached:1 },
  { hour:"10:00", created:18, completed:16, breached:0 },
  { hour:"11:00", created:14, completed:13, breached:0 },
  { hour:"12:00", created:10, completed:9,  breached:0 },
  { hour:"13:00", created:8,  completed:7,  breached:0 },
  { hour:"14:00", created:6,  completed:4,  breached:1 },
];

const TASK_BY_PRIORITY = [
  { name:"HIGH", value:9,  color:"#EF4444" },
  { name:"MED",  value:5,  color:"#F59E0B" },
  { name:"LOW",  value:2,  color:"#2563EB" },
];

const DEPT_COLORS = { police:"#2563EB", health:"#10B981", transport:"#F59E0B", municipal:"#7C3AED", disaster:"#EF4444", revenue:"#64748B", "cm office":"#0891B2" };

const statusStyle = s =>
  s === "approved"    ? "badge badge-success" :
  s === "in_progress" ? "badge badge-info"    :
  s === "pending"     ? "badge badge-warning" : "badge badge-critical";

const statusLabel = s =>
  ({ approved:"Approved", in_progress:"In Progress", pending:"Pending" }[s] || s);

// Impact/priority/threat-level vocabulary (CRITICAL/HIGH/MED/else) -> STATUS
// buckets, shared across the decision queue, cross-agency requests and Shahi
// Snan threat badges.
const IMPACT_BUCKET = { CRITICAL: STATUS_KEY.critical, HIGH: STATUS_KEY.serious, MED: STATUS_KEY.warning };
const impactBg = v => statusClasses(IMPACT_BUCKET[v] || STATUS_KEY.info);

const CAR_STATUS_BUCKET = { "In Progress": STATUS_KEY.warning, Approved: STATUS_KEY.good, Active: STATUS_KEY.info };
const carStatusBg = s => statusClasses(CAR_STATUS_BUCKET[s] || STATUS_KEY.neutral);

// War room log event types. "ai" keeps a local violet override — STATUS has
// no violet/purple bucket — and the default keeps its original slate-400
// (lighter than STATUS.neutral's slate-600) since it's a de-emphasized tag.
const logColor = t => {
  if (t === "ai")       return "text-violet-600";
  if (t === "decision") return STATUS.info.text;
  if (t === "escalate") return STATUS.critical.text;
  if (t === "action")   return STATUS.good.text;
  return "text-slate-400";
};

const logIcon = t =>
  t === "decision" ? "⚖️" : t === "ai" ? "🤖" : t === "escalate" ? "⬆️" : "✅";

// Priority breakdown legend (Task Creation vs Completion chart footer).
const PRIORITY_BUCKET = { HIGH: STATUS_KEY.critical, MED: STATUS_KEY.warning, LOW: STATUS_KEY.info };

// VIP movement status -> STATUS bucket (card border/bg + badge).
const VIP_STATUS_BUCKET = { Active: STATUS_KEY.warning, Approved: STATUS_KEY.info };
const vipBucket = s => VIP_STATUS_BUCKET[s] || STATUS_KEY.good;

// Officer board status -> STATUS bucket for the compact status badge (which
// has its own finer "Responding" tier than the row border tint below).
const OFFICER_BADGE_BUCKET = { Emergency: STATUS_KEY.critical, Responding: STATUS_KEY.serious, "In Progress": STATUS_KEY.warning };
const officerBadgeClass = s => statusClasses(OFFICER_BADGE_BUCKET[s] || STATUS_KEY.good);

// Command Authority Chain — escalation level (0 = field officer, 4 = CM/EOC)
// resolved through the shared numeric-threshold helper, then mapped to the
// specific 500-shade ring classes this timeline dot needs (STATUS only
// ships 100/200 shades for badges, not the bolder ring border).
const AUTHORITY_LEVEL_BANDS = [
  { min: 4, bucket: STATUS_KEY.critical },
  { min: 3, bucket: STATUS_KEY.serious },
  { min: 2, bucket: STATUS_KEY.warning },
  { min: 1, bucket: STATUS_KEY.info },
  { min: 0, bucket: STATUS_KEY.neutral },
];
const AUTHORITY_DOT_CLASS = {
  critical: "bg-red-100 border-red-500 text-red-700",
  serious:  "bg-orange-100 border-orange-500 text-orange-700",
  warning:  "bg-amber-100 border-amber-500 text-amber-700",
  info:     "bg-blue-100 border-blue-500 text-blue-700",
  neutral:  "bg-slate-100 border-slate-400 text-slate-600",
};

// ── component ─────────────────────────────────────────────────────
// Merged from the former standalone Unified Command + War Room pages: one
// command surface covering incident/crowd/zone command (map, incidents,
// zone risk, live feeds, officer roster, crowd flow prediction) alongside
// inter-agency workflow (decision queue, cross-agency requests, task board,
// Shahi Snan planning, authority chain, audit trail) so nothing that used
// to live on either page is missing from this one.
export default function WarRoom() {
  const { live: liveModel, twin, accuracy } = useIntelligence({ running: false });
  const t = useTranslate();
  const [incFilter,  setIncFilter]  = useState("All");
  const [expandedInc,setExpandedInc]= useState(null);
  const [alertTab,   setAlertTab]   = useState("feed");
  const [taskTab,    setTaskTab]    = useState("all");
  const [activeTab,  setActiveTab]  = useState("decisionQ");
  const [expandedSn, setExpandedSn] = useState(null);

  const filteredIncidents = incFilter === "All"
    ? activeIncidents
    : activeIncidents.filter(i => i.priority === incFilter);

  const allTasks = Object.entries(warRoomTasks).flatMap(([dept, tasks]) =>
    tasks.map(task => ({ ...task, dept }))
  );
  const filteredTasks = taskTab === "all" ? allTasks : allTasks.filter(task => task.dept === taskTab);

  // Department-level totals (the Agency Coordination Matrix + bottom bar
  // chart below both read agencyCoordination directly) — computed once here
  // so the KPI row above can never silently drift from those tables.
  const deptCompletedTotal = agencyCoordination.reduce((s, a) => s + a.completed, 0);
  const deptSlaBreachTotal = agencyCoordination.reduce((s, a) => s + a.slaBreaches, 0);

  // Officers currently part of a live escalation, shown alongside the full
  // duty roster below — same page now, so both views sit side by side.
  const escalatedOfficers = officerLocations.filter(o => o.status === "Emergency" || o.status === "Responding");
  const onEmergencyCount  = officerLocations.filter(o => o.status === "Emergency").length;
  const onDutyCount       = officerLocations.filter(o => o.status === "On Duty").length;

  return (
    <div className="space-y-8">
      <SectionHeader icon="Sword" title="War Room"
        subtitle="State-wide unified command · all agencies · zone-level risk · live incident management · decision approval queue · cross-agency requests · SITREP · escalation pipeline · Shahi Snan planning · responsibility matrix · command authority chain · VIP coordination · audit trail · SLA analytics"
        badge="18 ACTIVE"
      />


      <LiveMissionSection live={liveModel} twin={twin} accuracy={accuracy} />

      <StaticContentDivider />
      {/* KPI row — incident command */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard title="Active Incidents"  value="18"      icon="Zap"          color="red"    index={0} live />
        <MetricCard title="Escalations"       value="6"       icon="TrendingUp"   color="amber"  index={1} />
        <MetricCard title="Resolved Today"    value="57"      icon="CheckCircle"  color="green"  index={2} />
        <MetricCard title="Avg Response"      value="8.4 min" icon="Clock"        color="blue"   index={3} />
        <MetricCard title="Units Deployed"    value="2,840"   icon="Shield"       color="purple" index={4} />
        <MetricCard title="SLA at Risk"       value="2"       icon="AlertTriangle" color="red"   index={5} />
      </div>

      {/* KPI row — inter-agency task command */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Active Tasks"      value={allTasks.length.toString()}         icon="ClipboardList"  color="blue"   index={0} live />
        <MetricCard title="Pending Approval"  value={DECISION_QUEUE.length.toString()}   icon="Clock"          color="amber"  index={1} live />
        <MetricCard title="Completed Today"   value={deptCompletedTotal.toString()}      icon="CheckCircle"    color="green"  index={2} />
        <MetricCard title="SLA Breaches"      value={deptSlaBreachTotal.toString()}      icon="AlertTriangle"  color="red"    index={3} />
        <MetricCard title="Agencies Online"   value={agencyCoordination.length.toString()} icon="Building2"    color="blue"   index={4} />
        <MetricCard title="Decisions Pending" value={DECISION_QUEUE.length.toString()}   icon="Target"         color="amber"  index={5} live />
        <MetricCard title="AI Actions Q."     value="3"   icon="Brain"          color="purple" index={6} />
        <MetricCard title="Cross-Agency Req." value={CROSS_AGENCY_REQUESTS.length.toString()} icon="GitBranch" color="blue"  index={7} />
      </div>

      {/* SITREP Banner */}
      <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("LIVE SITREP — 14:33 IST")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Simhastha 2027 · Day 3 Operations · Maha Kumbh AI Command Centre, Nashik")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("6,84,210 pilgrims active across 6 zones · Crowd risk 5.8/10 · 18 incidents open, 57 resolved today · All 7 agencies operational · Next Shahi Snan T–18 hours")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
            {[
              { label:"Crowd Risk",  value:"5.8",  unit:"/10" },
              { label:"AI Uptime",   value:"99.8",  unit:"%" },
              { label:"SLA Met",     value:"96.5",  unit:"%" },
            ].map((s, i) => {
              const c = googleColor(i);
              return (
                <div key={s.label} className="rounded-xl px-4 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-xl font-black" style={{ color:c.text }}>{s.value}<span className="text-xs">{s.unit}</span></div>
                  <div className="text-[10px]" style={{ color:c.text }}>{t(s.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Daily SITREP — auto-generated, human verified */}
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
        className="rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 gov-gradient rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-sm font-black text-slate-900">{t("Daily SITREP")} — {warRoomSITREP.generatedAt}</h3>
              <div className="flex items-center gap-2">
                <span className="badge badge-live text-[9px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot mr-1" />{t("Auto-Generated")}</span>
                <span className="badge badge-info text-[9px]">{t("AI + Human Verified")}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              {[
                { label:"Crowd Status",     body:warRoomSITREP.crowdStatus    },
                { label:"Incident Summary", body:warRoomSITREP.incidentSummary},
                { label:"Resource Status",  body:warRoomSITREP.resourceStatus },
                { label:"Next Shahi Snan",  body:warRoomSITREP.nextShahiSnan  },
              ].map(s => (
                <div key={s.label}>
                  <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t(s.label)}</div>
                  <p className="text-xs text-slate-700 leading-relaxed">{t(s.body)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-blue-200 pt-3">
              <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Immediate Action Items")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {warRoomSITREP.actionItems.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <ChevronRight size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{t(a)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Command Map + Incidents */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-900">{t("Multi-Agency Command Map")}</h3>
            <div className="flex items-center gap-2">
              <span className="badge badge-live"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />{t("Live")}</span>
              <button className="btn-ghost text-xs flex items-center gap-1"><RefreshCw size={12} /> {t("Refresh")}</button>
            </div>
          </div>
          <MapPlaceholder height={440} showHeatmap />
          {/* Zone quick stats below map */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
            {crowdFlowByZone.slice(0,3).map(z => (
              <div key={z.zone} className={`rounded-xl p-2.5 border text-center ${
                z.risk === "critical" ? `bg-red-50 ${STATUS.critical.border}` :
                z.risk === "high"     ? `bg-orange-50 ${STATUS.serious.border}` : `bg-amber-50 ${STATUS.warning.border}`
              }`}>
                <div className="text-[9px] font-bold text-slate-600 truncate">{t(z.zone)}</div>
                <div className={`text-lg font-black ${
                  z.risk === "critical" ? STATUS.critical.text : z.risk === "high" ? STATUS.serious.text : STATUS.warning.text
                }`}>{z.current.toLocaleString()}</div>
                <div className="text-[9px] text-slate-500">{t("pilgrims")} · {z.density}/m²</div>
              </div>
            ))}
          </div>
        </div>

        {/* Incident panel */}
        <div className="xl:col-span-2 card p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black text-slate-900">{t("Active Incidents")}</h3>
            <div className="flex gap-1">
              {["All","CRITICAL","HIGH"].map(f => (
                <button key={f} onClick={() => setIncFilter(f)}
                  className={`text-[9px] font-black px-2.5 py-1 rounded-lg transition-colors ${
                    incFilter === f ? "gov-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}>{t(f)}</button>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-slate-400 -mt-2 mb-2">
            {t("Showing")} {activeIncidents.length} {t("of")} {topStats.openCases} {t("open incidents — full queue includes lower-priority items not requiring live command attention")}
          </p>
          <div className="flex-1 space-y-2 overflow-y-auto max-h-[560px] pr-0.5">
            {filteredIncidents.map((inc, i) => {
              const isOpen = expandedInc === inc.id;
              const detail = INCIDENT_DETAIL[inc.id];
              return (
                <motion.div key={inc.id}
                  initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                  className={`rounded-xl border transition-all ${
                    isOpen ? "border-blue-300 shadow-card-hover" :
                    inc.priority === "CRITICAL" ? `${STATUS.critical.border} bg-red-50/30 hover:border-red-300` :
                    "border-slate-200 hover:border-blue-200"
                  }`}
                >
                  {/* Header row */}
                  <div className="p-3 cursor-pointer" onClick={() => setExpandedInc(isOpen ? null : inc.id)}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                          <span className="text-[9px] font-black text-slate-400">{inc.id}</span>
                          <span className={priorityClass(inc.priority)}>{t(inc.priority)}</span>
                          <span className={`text-[10px] ${statusTextColor(inc.status)}`}>{t(inc.status)}</span>
                        </div>
                        <p className="text-xs font-bold text-slate-800 leading-snug">{t(inc.title)}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <MapPin size={9} />{t(inc.zone)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {isOpen ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[9px] text-slate-500">
                      <span className="flex items-center gap-1"><Clock size={9} />{inc.elapsed} {t("elapsed")}</span>
                      <span>{inc.dept} · SLA: <strong className={slaUrgency(inc.eta)}>{t(inc.eta)}</strong></span>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  <AnimatePresence>
                    {isOpen && detail && (
                      <motion.div
                        initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                        exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
                        className="overflow-hidden border-t border-slate-200"
                      >
                        <div className="p-3 space-y-3 bg-slate-50 rounded-b-xl">
                          <p className="text-[10px] text-slate-600 leading-relaxed">{t(detail.description)}</p>

                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Assigned Units")}</div>
                            <div className="flex flex-wrap gap-1">
                              {detail.units.map(u => (
                                <span key={u} className="badge badge-info text-[9px]">{u}</span>
                              ))}
                            </div>
                          </div>

                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1.5">{t("Action Log")}</div>
                            <div className="space-y-1.5">
                              {detail.actions.map((a, j) => (
                                <div key={j} className="flex gap-2 text-[10px]">
                                  <span className="text-slate-400 flex-shrink-0 font-mono">{a.time}</span>
                                  <span className="text-blue-700 font-bold flex-shrink-0">{a.by}</span>
                                  <span className="text-slate-600">{t(a.act)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Zone Risk Matrix */}
      <ChartCard title="Zone Risk Matrix"
        subtitle="Real-time crowd density, risk level, incident count and unit deployment per zone">
        <div className="overflow-x-auto">
          <table className="gov-table">
            <thead>
              <tr>
                <th>{t("Zone")}</th>
                <th>{t("Pilgrims")}</th>
                <th>{t("Density / m²")}</th>
                <th>{t("Crowd Trend")}</th>
                <th>{t("Risk Level")}</th>
                <th>{t("Open Incidents")}</th>
                <th>{t("Units Deployed")}</th>
                <th>{t("Route Status")}</th>
              </tr>
            </thead>
            <tbody>
              {crowdFlowByZone.map((z, i) => (
                <motion.tr key={z.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-bold text-slate-900">{t(z.zone)}</td>
                  <td className="font-black text-slate-800">{z.current.toLocaleString()}</td>
                  <td>
                    <span className={`font-black text-sm ${STATUS[thresholdStatus(z.density, [{ min:9, bucket:"critical" }, { min:7, bucket:"serious" }, { min:5, bucket:"warning" }, { min:0, bucket:"good" }])].text}`}>{z.density}</span>
                  </td>
                  <td>
                    <span className={`flex items-center gap-1 text-xs font-semibold ${
                      z.trend === "rising"  ? STATUS.critical.text :
                      z.trend === "stable"  ? STATUS.warning.text  : STATUS.good.text
                    }`}>
                      {z.trend === "rising"  ? <TrendingUp size={11} />  :
                       z.trend === "falling" ? <TrendingDown size={11} /> :
                       <Activity size={11} />}
                      {t(z.trend)}
                    </span>
                  </td>
                  <td><span className={`badge text-[9px] ${riskBg(z.risk)}`}>{t(z.risk.toUpperCase())}</span></td>
                  <td>
                    <span className={`font-black ${
                      z.risk === "critical" ? STATUS.critical.text : z.risk === "high" ? STATUS.serious.text : "text-slate-600"
                    }`}>
                      {z.risk === "critical" ? 3 : z.risk === "high" ? 2 : z.risk === "moderate" ? 1 : 0}
                    </span>
                  </td>
                  <td className="font-semibold text-blue-700">
                    {z.risk === "critical" ? 420 : z.risk === "high" ? 340 : z.risk === "moderate" ? 220 : 140}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full" style={{
                          width:`${Math.round((z.current/z.capacity)*100)}%`,
                          background: statusHex(riskBucket(z.risk))
                        }} />
                      </div>
                      <span className="text-[10px] font-bold">{Math.round((z.current/z.capacity)*100)}%</span>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Decision Queue + Cross-Agency + War Room Log + VIP tabs */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-black text-slate-900">{t("Command Operations Center")}</h3>
          <div className="flex gap-1 flex-wrap">
            {[
              { id:"decisionQ",  label:"Decision Queue" },
              { id:"crossAgency",label:"Cross-Agency Requests" },
              { id:"warLog",     label:"War Room Log" },
              { id:"vip",        label:"VIP Coordination" },
            ].map(tb => (
              <button key={tb.id} onClick={() => setActiveTab(tb.id)}
                className={`text-[9px] font-black px-3 py-1.5 rounded-lg transition-colors ${
                  activeTab === tb.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(tb.label)}</button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "decisionQ" && (
            <motion.div key="dq" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-2">
              <p className="text-[9px] text-violet-700 font-bold mb-2 px-1">
                {t("⚠ All AI-generated recommendations require human officer approval before action is taken — DPDP 2023 / Maha Kumbh AI Protocol")}
              </p>
              {DECISION_QUEUE.map((d, i) => (
                <motion.div key={d.id}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                  className={`p-3 rounded-xl border ${
                    d.impact === "HIGH" || d.impact === "CRITICAL"
                      ? "border-amber-200 bg-amber-50/20"
                      : "border-slate-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className="font-mono text-[9px] text-slate-400">{d.id}</span>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${impactBg(d.impact)}`}>{t(d.impact)}</span>
                        <span className="badge badge-navy text-[8px]">{t(d.type)}</span>
                      </div>
                      <p className="text-xs font-black text-slate-900 mb-1">{t(d.title)}</p>
                      <div className="flex items-center gap-3 text-[9px] text-slate-500 flex-wrap">
                        <span>{t("Req by")}: <strong className="text-slate-700">{t(d.reqBy)}</strong></span>
                        <span>{t("Auth")}: <strong className="text-blue-700">{t(d.authority)}</strong></span>
                        <span className="flex items-center gap-1"><Clock size={8} />{t("Deadline")}: <strong className="text-red-600">{d.deadline}</strong></span>
                        <span className="text-violet-700 font-bold">{t("AI conf")}: {d.aiConf}%</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button className="text-[9px] font-black px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200 transition-colors">
                        {t("Approve")}
                      </button>
                      <button className="text-[9px] font-black px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors">
                        {t("Escalate ↑")}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "crossAgency" && (
            <motion.div key="ca" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-2">
              {CROSS_AGENCY_REQUESTS.map((r, i) => (
                <motion.div key={r.id}
                  initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                  className="p-3 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className="font-mono text-[9px] text-slate-400">{r.id}</span>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${impactBg(r.priority)}`}>{t(r.priority)}</span>
                        <span className="text-[9px] text-slate-400 font-mono">{r.time}</span>
                      </div>
                      <p className="text-xs font-semibold text-slate-800 mb-1">{t(r.request)}</p>
                      <div className="flex items-center gap-1.5 text-[9px]">
                        <span className="font-bold px-1.5 py-0.5 rounded-md text-[8px]"
                          style={{ background:(DEPT_COLORS[r.from.split(" ")[0].toLowerCase()] || "#64748B")+"20",
                                   color: DEPT_COLORS[r.from.split(" ")[0].toLowerCase()] || "#64748B" }}>
                          {t(r.from)}
                        </span>
                        <ArrowRight size={10} className="text-slate-400" />
                        <span className="font-bold px-1.5 py-0.5 rounded-md text-[8px]"
                          style={{ background:(DEPT_COLORS[r.to.split(" ")[0].toLowerCase()] || "#64748B")+"20",
                                   color: DEPT_COLORS[r.to.split(" ")[0].toLowerCase()] || "#64748B" }}>
                          {t(r.to)}
                        </span>
                      </div>
                    </div>
                    <span className={`text-[9px] font-black px-2 py-1 rounded-full flex-shrink-0 ${carStatusBg(r.status)}`}>{t(r.status)}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "warLog" && (
            <motion.div key="wl" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-1.5 max-h-[440px] overflow-y-auto pr-0.5">
              {WAR_ROOM_LOG.map((l, i) => (
                <motion.div key={i}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
                  className="flex items-start gap-3 p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <span className="font-mono text-[9px] text-slate-400 flex-shrink-0 w-10">{l.time}</span>
                  <span className="text-[10px] flex-shrink-0">{logIcon(l.type)}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-black" style={{ color: DEPT_COLORS[l.dept.toLowerCase()] || "#64748B" }}>
                      {t(l.actor)}
                    </span>
                    <span className="text-[9px] text-slate-400 ml-1">({t(l.dept)})</span>
                    <span className="text-[9px] text-slate-500 ml-1">—</span>
                    <span className="text-[10px] text-slate-700 ml-1">{t(l.msg)}</span>
                  </div>
                  <span className={`text-[8px] font-black flex-shrink-0 ${logColor(l.type)}`}>{t(l.type)}</span>
                </motion.div>
              ))}
              <p className="text-[9px] text-violet-600 font-bold pt-2 border-t border-slate-100">
                {t("⚠ All actions logged for DPDP 2023 audit trail. AI actions marked separately and require human confirmation.")}
              </p>
            </motion.div>
          )}

          {activeTab === "vip" && (
            <motion.div key="vip" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-3">
              {VIP_MOVEMENT.map((v, i) => (
                <motion.div key={v.vip}
                  initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                  className={`p-4 rounded-xl border ${
                    v.status === "Active" ? `${STATUS.warning.border} bg-amber-50/20` :
                    v.status === "Approved" ? `${STATUS.info.border} bg-blue-50/10` : `${STATUS.good.border} bg-emerald-50/10`
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Star size={12} className="text-amber-500" />
                        <span className="text-sm font-black text-slate-900">{t(v.vip)}</span>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(vipBucket(v.status))}`}>{t(v.status)}</span>
                        <span className="badge badge-info text-[8px]">{t("Security")} {v.security}</span>
                      </div>
                      <div className="text-[10px] text-slate-600 space-y-0.5">
                        <div className="flex items-center gap-1.5"><Clock size={9} className="text-slate-400" />{t("ETA")}: <strong>{v.eta}</strong></div>
                        <div className="flex items-center gap-1.5"><MapPin size={9} className="text-slate-400" />{t(v.route)}</div>
                        <div className="flex items-center gap-1.5"><Shield size={9} className="text-blue-500" />{t("Escort")}: {t(v.escort)}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Route saturation + Live feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Route Saturation */}
        <ChartCard title="Route Saturation Monitor"
          subtitle="Current load per major entry/exit corridor" live>
          <div className="space-y-3">
            {routeSaturation.map((r, i) => (
              <motion.div key={r.route}
                initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700 truncate flex-1 mr-2">{t(r.route)}</span>
                  <span className={`text-xs font-black flex-shrink-0 ${STATUS[riskBucket(r.status)].text}`}>{r.load}%</span>
                </div>
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-3 rounded-full relative"
                    initial={{ width:0 }} animate={{ width:`${r.load}%` }} transition={{ duration:0.8, delay: i * 0.07 }}
                    style={{ background: statusHex(riskBucket(r.status)) }}
                  >
                    {r.load >= 85 && (
                      <span className="absolute right-1 top-0 bottom-0 flex items-center text-[8px] font-black text-white">!</span>
                    )}
                  </motion.div>
                </div>
                {r.load >= 85 && (
                  <p className={`text-[9px] ${STATUS.critical.text} font-bold mt-0.5 flex items-center gap-1`}>
                    <AlertTriangle size={8} /> {t("Overload — crowd diversion recommended")}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </ChartCard>

        {/* Alert feed / Comms log */}
        <div className="card p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black text-slate-900">{t("Live Feeds")}</h3>
            <div className="flex gap-1">
              {[{id:"feed",label:"Alert Feed"},{id:"timeline",label:"Command Log"}].map(tab => (
                <button key={tab.id} onClick={() => setAlertTab(tab.id)}
                  className={`text-[9px] font-black px-2.5 py-1 rounded-lg transition-colors ${
                    alertTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                  }`}>{t(tab.label)}</button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {alertTab === "feed" ? (
              <motion.div key="feed" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                className="flex-1 space-y-1.5 overflow-y-auto max-h-[340px]">
                {alertFeed.map((a, i) => (
                  <motion.div key={a.id}
                    initial={{ opacity:0, x:6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
                    className={`flex items-start gap-2 p-2.5 rounded-xl border text-xs ${
                      a.type === "danger"  ? `bg-red-50 ${STATUS.critical.border}` :
                      a.type === "warning" ? `bg-amber-50 ${STATUS.warning.border}` :
                      a.type === "success" ? `bg-emerald-50 ${STATUS.good.border}` : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <span className="text-[9px] text-slate-400 font-mono flex-shrink-0 mt-0.5">{a.time}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold leading-snug ${
                        a.type === "danger"  ? STATUS.critical.text :
                        a.type === "warning" ? STATUS.warning.text :
                        a.type === "success" ? STATUS.good.text : "text-slate-700"
                      }`}>{t(a.title)}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[9px] text-slate-500">{a.dept}</span>
                        <span className={`text-[9px] font-black ${
                          a.priority === "HIGH" ? STATUS.serious.text :
                          a.priority === "MED"  ? STATUS.warning.text : "text-slate-400"
                        }`}>{t(a.priority)}</span>
                      </div>
                    </div>
                    <span className={`text-[8px] font-black flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      statusClasses(a.status === "Active" ? "critical" :
                        (a.status === "Closed" || a.status === "Auto-Closed" || a.status === "Validated" || a.status === "Published") ? "good" : "info")
                    }`}>{t(a.status)}</span>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div key="timeline" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                className="flex-1 space-y-2 overflow-y-auto max-h-[340px]">
                {commandTimeline.map((e, i) => {
                  const { cls, Icon } = timelineIcon(e.type);
                  return (
                    <motion.div key={i}
                      initial={{ opacity:0, x:6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
                      className="flex items-start gap-2.5"
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${cls}`}>
                        <Icon size={11} />
                      </div>
                      <div className="flex-1 pb-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[9px] font-mono text-slate-400">{e.time}</span>
                          <span className="badge badge-info text-[8px]">{e.dept}</span>
                        </div>
                        <p className="text-[10px] text-slate-700 font-semibold leading-snug">{t(e.msg)}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Response timeline + Resource deployment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Incident Response Timeline" subtitle="Hourly volume · resolved · escalated" live>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={responseTimeline} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="ucIncGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Area type="monotone" dataKey="incidents" fill="url(#ucIncGrad)" stroke={chartColor(0)} strokeWidth={2.5} name={t("Total")} />
              <Bar dataKey="resolved"  fill={statusHex("good")} radius={[3,3,0,0]} name={t("Resolved")} />
              <Bar dataKey="escalated" fill={statusHex("critical")} radius={[3,3,0,0]} name={t("Escalated")} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Zone Resource Deployment" subtitle="Police · Health · Volunteers by zone">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={resourceDeployment} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="zone" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="police"     fill={chartColor(0)} radius={[3,3,0,0]} name="Police" />
              <Bar dataKey="health"     fill={chartColor(1)} radius={[3,3,0,0]} name="Health" />
              <Bar dataKey="volunteers" fill={chartColor(2)} radius={[3,3,0,0]} name={t("Volunteers")} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Task Board */}
      <ChartCard title="Command Task Board — All Departments"
        subtitle="Full inter-agency task register with ownership, SLA and approval status"
        action={
          <div className="flex gap-1 flex-wrap">
            {["all","police","health","transport","municipal","disaster"].map(tab => (
              <button key={tab} onClick={() => setTaskTab(tab)}
                className={`text-[9px] font-black px-2.5 py-1 rounded-lg transition-colors capitalize ${
                  taskTab === tab ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(tab)}</button>
            ))}
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th>{t("Task ID")}</th><th>{t("Title")}</th><th>{t("Department")}</th>
                <th>{t("Priority")}</th><th>{t("Owner")}</th><th>SLA</th><th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task, i) => (
                <motion.tr key={task.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
                  <td className="font-black text-blue-700 font-mono">{task.id}</td>
                  <td className="font-semibold text-slate-800">{t(task.title)}</td>
                  <td>
                    <span className="font-bold text-xs capitalize px-2 py-0.5 rounded-full"
                      style={{ background:(DEPT_COLORS[task.dept]||"#64748B")+"20", color:DEPT_COLORS[task.dept]||"#64748B" }}>
                      {t(task.dept)}
                    </span>
                  </td>
                  <td><span className={task.priority === "HIGH" ? "priority-high" : task.priority === "MED" ? "priority-medium" : "priority-low"}>{t(task.priority)}</span></td>
                  <td><User size={10} className="inline text-blue-600 mr-1" />{t(task.owner)}</td>
                  <td><Clock size={10} className="inline text-slate-400 mr-1" /><span className="font-bold">{task.sla}</span></td>
                  <td><span className={statusStyle(task.status)}>{t(statusLabel(task.status))}</span></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Field Officer Board + Officers In Active Escalation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Field Officer Board" subtitle="Full duty roster — live location, task and status for every officer">
          <div className="space-y-2">
            {officerLocations.map((o, i) => (
              <motion.div key={o.unit}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                className={`flex items-start justify-between gap-2 p-3 rounded-xl border ${
                  o.status === "Emergency"   ? `${STATUS.critical.border} bg-red-50` :
                  o.status === "Responding"  ? `${STATUS.serious.border} bg-orange-50` : "border-slate-200"
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 ${
                    o.status === "Emergency"  ? statusClasses("critical") :
                    o.status === "Responding" ? statusClasses("serious") : statusClasses("info")
                  }`}>{o.unit.slice(-2)}</div>
                  <div>
                    <div className="text-xs font-bold text-slate-800">{o.unit}</div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <MapPin size={9} />{t(o.zone)}
                    </div>
                    <div className="text-[10px] text-blue-700 font-semibold mt-0.5">{t(o.task)}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                    o.status === "Emergency"   ? statusClasses("critical") :
                    o.status === "Responding"  ? statusClasses("serious") :
                    o.status === "In Progress" ? statusClasses("warning") : statusClasses("good")
                  }`}>{t(o.status)}</span>
                  <div className="text-[9px] text-slate-400 mt-1 flex items-center justify-end gap-0.5"><Clock size={8} />{o.lastUpdate}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Officers In Active Escalation" subtitle="Only officers currently part of a live escalation — the full duty roster is alongside">
          <div className="space-y-2">
            {escalatedOfficers.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">{t("No officers currently escalated — all clear")}</p>
            )}
            {escalatedOfficers.map((o, i) => (
              <motion.div key={o.unit}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`flex items-center gap-3 p-2.5 rounded-xl border ${
                  o.status === "Emergency"    ? `${STATUS.critical.border} bg-red-50/20`    :
                  o.status === "Responding"   ? `${STATUS.warning.border} bg-amber-50/20`   : "border-slate-200"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-black text-slate-900">{o.unit}</span>
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${officerBadgeClass(o.status)}`}>{t(o.status)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[9px] text-slate-500 flex-wrap mt-0.5">
                    <span className="flex items-center gap-1">
                      <MapPin size={8} />
                      <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline">{t(o.zone)}</Link>
                    </span>
                    <span>{t(o.task)}</span>
                  </div>
                </div>
                <div className="text-[9px] text-slate-400 flex-shrink-0 font-mono">{o.lastUpdate}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-xl font-black text-blue-700">{officerLocations.length}</div><div className="text-[9px] text-slate-500">{t("Total Officers")}</div></div>
            <div><div className="text-xl font-black text-amber-600">{onEmergencyCount}</div><div className="text-[9px] text-slate-500">{t("On Emergency")}</div></div>
            <div><div className="text-xl font-black text-emerald-600">{onDutyCount}</div><div className="text-[9px] text-slate-500">{t("On Duty")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Agency Coordination Matrix + Task Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Agency Coordination Matrix"
          subtitle="Task completion · SLA performance · last contact per department">
          <div className="overflow-x-auto">
            <table className="gov-table w-full text-xs">
              <thead>
                <tr>
                  <th>{t("Department")}</th><th className="text-center">{t("Tasks")}</th>
                  <th className="text-center">{t("Done")}</th><th className="text-center">{t("Pending")}</th>
                  <th className="text-center">SLA</th><th>{t("Completion")}</th>
                </tr>
              </thead>
              <tbody>
                {agencyCoordination.map((a, i) => {
                  const pct = Math.round((a.completed / a.tasks) * 100);
                  return (
                    <motion.tr key={a.dept} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}
                      className={`border-l-2`} style={{ borderLeftColor: DEPT_COLORS[a.dept.toLowerCase().split(" ")[0]] || "#64748B" }}>
                      <td className="font-bold text-slate-900">{t(a.dept)}</td>
                      <td className="text-center font-semibold">{a.tasks}</td>
                      <td className="text-center font-black text-emerald-600">{a.completed}</td>
                      <td className="text-center"><span className={a.pending > 0 ? "text-amber-600 font-bold" : "text-slate-400"}>{a.pending}</span></td>
                      <td className="text-center">
                        <span className={`font-bold ${a.slaBreaches > 0 ? "text-red-600" : "text-emerald-600"}`}>
                          {a.slaBreaches > 0 ? `⚠ ${a.slaBreaches}` : "✓"}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <motion.div className="h-2 rounded-full" style={{ background: DEPT_COLORS[a.dept.toLowerCase().split(" ")[0]] || "#64748B" }}
                              initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:0.8 }} />
                          </div>
                          <span className="text-[10px] font-black text-slate-700 flex-shrink-0">{pct}%</span>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="Task Creation vs Completion — Hourly"
          subtitle="War room task throughput with SLA breach incidents">
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={TASK_TREND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="hour" tick={{ ...axisTick, fontSize:9 }} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="created"   fill={chartColor(0)} radius={[3,3,0,0]} name={t("Created")} />
              <Bar dataKey="completed" fill={chartColor(1)} radius={[3,3,0,0]} name={t("Completed")} />
              <Line type="monotone" dataKey="breached" stroke={statusHex(STATUS_KEY.critical)} strokeWidth={2} dot={{ r:4 }} name={t("SLA Breach")} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
            {TASK_BY_PRIORITY.map(p => (
              <div key={p.name} className="flex items-center gap-1.5 text-[10px]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background:statusHex(PRIORITY_BUCKET[p.name]) }} />
                <span className="text-slate-600">{t(p.name)} {t("Priority")}:</span>
                <span className="font-black text-slate-800">{p.value} {t("tasks")}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Multi-Agency Status Table */}
      <ChartCard title="Multi-Agency Operational Status"
        subtitle="Live deployment, alerts and resolution rate per government department">
        <div className="overflow-x-auto">
          <table className="gov-table">
            <thead>
              <tr>
                <th>{t("Department")}</th>
                <th>{t("Deployed / Total")}</th>
                <th>{t("Deployment %")}</th>
                <th>{t("Open Alerts")}</th>
                <th>{t("Resolution Rate")}</th>
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {departmentStatus.map((d, i) => (
                <motion.tr key={d.name} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-6 rounded-full flex-shrink-0" style={{ background:d.color }} />
                      <span className="font-bold">{d.name}</span>
                    </div>
                  </td>
                  <td className="font-semibold">{d.active.toLocaleString()} / {d.total.toLocaleString()}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full" style={{
                          width:`${Math.round((d.active/d.total)*100)}%`, background:d.color
                        }} />
                      </div>
                      <span className="text-xs font-bold">{Math.round((d.active/d.total)*100)}%</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${d.alerts > 5 ? "badge-critical" : d.alerts > 2 ? "badge-warning" : "badge-success"}`}>{d.alerts}</span>
                  </td>
                  <td><span className="text-emerald-600 font-bold">{d.resolved}%</span></td>
                  <td>
                    <span className="badge badge-live">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />{t("Live")}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Responsibility Matrix */}
      <ChartCard title="Inter-Agency Responsibility Matrix"
        subtitle="Domain ownership · primary + secondary agencies · escalation triggers and chain — Simhastha 2027">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Domain")}</th>
                <th className="text-left text-blue-700">{t("Primary")}</th>
                <th className="text-left text-amber-700">{t("Secondary")}</th>
                <th className="text-left">{t("Trigger Condition")}</th>
                <th className="text-left">{t("Escalation Chain")}</th>
              </tr>
            </thead>
            <tbody>
              {RESPONSIBILITY_MATRIX.map((r, i) => (
                <motion.tr key={r.domain} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-black text-slate-900 whitespace-nowrap">{t(r.domain)}</td>
                  <td>
                    <span className="font-bold px-2 py-0.5 rounded-full text-[9px]"
                      style={{ background:(DEPT_COLORS[r.primary.toLowerCase().split(" ")[0]]||"#64748B")+"20",
                               color:DEPT_COLORS[r.primary.toLowerCase().split(" ")[0]]||"#64748B" }}>
                      {t(r.primary)}
                    </span>
                  </td>
                  <td>
                    <span className="font-bold px-2 py-0.5 rounded-full text-[9px]"
                      style={{ background:(DEPT_COLORS[r.secondary.toLowerCase().split(" ")[0]]||"#64748B")+"15",
                               color:DEPT_COLORS[r.secondary.toLowerCase().split(" ")[0]]||"#64748B" }}>
                      {t(r.secondary)}
                    </span>
                  </td>
                  <td className="text-[10px] text-amber-700 font-semibold">{t(r.trigger)}</td>
                  <td className="text-[10px] text-slate-600">{t(r.escalation)}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Shahi Snan Planning */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Shahi Snan Planning Board — Simhastha 2027")}</h3>
        <div className="space-y-2">
          {SHAHI_SNAN_PLAN.map((s, i) => {
            const isOpen = expandedSn === s.date;
            return (
              <motion.div key={s.date}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`rounded-2xl border cursor-pointer transition-all ${
                  s.threatLevel === "CRITICAL" ? "border-red-200 bg-red-50/20" :
                  s.threatLevel === "HIGH" ? "border-orange-200 bg-orange-50/10" : "border-slate-200"
                } ${isOpen ? "shadow-card-hover" : ""}`}
                onClick={() => setExpandedSn(isOpen ? null : s.date)}
              >
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Calendar size={14} className="text-blue-600" />
                      <span className="text-sm font-black text-slate-900">{t(s.name)}</span>
                      <span className="text-[9px] font-bold text-slate-500">{s.date}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${impactBg(s.threatLevel)}`}>{t(s.threatLevel)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
                      <span>{t("Est. crowd")}: <strong className="text-blue-700">{s.crowdEst}</strong></span>
                      <span className="flex items-center gap-1"><Clock size={9} />{s.daysAway} {t("days away")}</span>
                    </div>
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className="text-2xl font-black text-blue-700">{s.readiness}%</div>
                    <div className="text-[9px] text-slate-500">{t("readiness")}</div>
                    <div className="w-20 h-2 rounded-full bg-slate-100 mt-1 overflow-hidden mx-auto">
                      <motion.div className={`h-2 rounded-full ${s.readiness >= 75 ? "bg-emerald-500" : s.readiness >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                        initial={{ width:0 }} animate={{ width:`${s.readiness}%` }} transition={{ duration:0.8 }} />
                    </div>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/60 rounded-b-2xl">
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Pending Preparation Tasks")}</div>
                          <div className="space-y-1.5">
                            {s.pending.map((p, idx) => (
                              <div key={idx} className="flex items-start gap-1.5">
                                <div className="w-3.5 h-3.5 rounded border border-amber-300 flex-shrink-0 mt-0.5 bg-amber-50" />
                                <span className="text-[10px] text-slate-700">{t(p)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Completed Preparation")}</div>
                          <div className="space-y-1.5">
                            {s.done.map((d, idx) => (
                              <div key={idx} className="flex items-start gap-1.5">
                                <CheckCircle size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                                <span className="text-[10px] text-emerald-700 line-through">{t(d)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Command Authority Chain */}
      <ChartCard title="Command Authority Chain"
        subtitle="Authorization levels · who can approve what · 2FA requirements · Simhastha 2027 governance">
        <div className="relative pl-6">
          <div className="absolute left-2.5 top-0 bottom-0 w-px bg-slate-200" />
          {COMMAND_AUTHORITY.map((c, i) => (
            <motion.div key={c.level}
              initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.08 }}
              className="relative mb-3 last:mb-0"
            >
              <div className={`absolute -left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center text-[8px] font-black ${AUTHORITY_DOT_CLASS[thresholdStatus(i, AUTHORITY_LEVEL_BANDS)]}`}>{i + 1}</div>
              <div className={`p-3 rounded-xl border ml-2 ${c.bg} border-slate-200`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`text-xs font-black ${c.color}`}>{t(c.level)}</span>
                      <span className="text-[10px] text-slate-500">({t(c.holder)})</span>
                      {c.twoFA && (
                        <span className="flex items-center gap-0.5 text-[8px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded-full">
                          <Lock size={8} /> 2FA
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-600">{t(c.scope)}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </ChartCard>

      {/* Audit Log + Department Task Load */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Maha Kumbh AI Audit Log — Recent Entries"
          subtitle="Full DPDP 2023 compliant audit trail of all officer and AI actions" live>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {auditLogEntries.map((a, i) => (
              <motion.div key={a.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.05 }}
                className="flex items-start gap-2 p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <span className="font-mono text-[8px] text-slate-400 w-14 flex-shrink-0 mt-0.5">{a.time}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <span className="font-mono text-[8px] text-slate-400">{a.id}</span>
                    <span className="text-[9px] font-black text-blue-700">{t(a.user)}</span>
                    <span className="badge badge-info text-[7px]">{t(a.role)}</span>
                  </div>
                  <p className="text-[10px] text-slate-700">{t(a.action)}</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">{t("Resource")}: {a.resource}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-[9px] text-violet-600 font-bold mt-2 pt-2 border-t border-slate-100">
            {t("22,840 audit log entries recorded today · All entries immutable · DPDP 2023 compliant")}
          </p>
        </ChartCard>

        <ChartCard title="Department Task Load Overview"
          subtitle="Total, completed and pending tasks across all 7 War Room departments">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={agencyCoordination} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="dept" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="tasks"     fill={chartColor(0)} radius={[3,3,0,0]} name={t("Total Tasks")} />
              <Bar dataKey="completed" fill={chartColor(1)} radius={[3,3,0,0]} name={t("Completed")} />
              <Bar dataKey="pending"   fill={chartColor(2)} radius={[3,3,0,0]} name={t("Pending")} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Crowd Flow Prediction */}
      <ChartCard title="Peak Snana Analytics — Crowd Flow Prediction"
        subtitle="Actual vs predicted pilgrim count · risk index · today's full-day forecast" live>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={crowdTrendData} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="time" tick={axisTick} />
            <YAxis yAxisId="left" tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
            <YAxis yAxisId="right" orientation="right" domain={[0,10]} tick={axisTick} />
            <Tooltip content={<ChartTooltip formatter={(v, n) => [n === t("Risk Index") ? `${v} / 10` : v?.toLocaleString(), n]} />} />
            <Legend wrapperStyle={legendStyle} />
            <Area yAxisId="left"  type="monotone" dataKey="actual"    fill={chartColor(0)} fillOpacity={0.15} stroke={chartColor(0)} strokeWidth={2.5} name={t("Actual")} />
            <Area yAxisId="left"  type="monotone" dataKey="predicted" fill={chartColor(6)} fillOpacity={0.15} stroke={chartColor(6)} strokeWidth={2} strokeDasharray="5 3" name={t("Predicted")} />
            <Line yAxisId="right" type="monotone" dataKey="risk"      stroke={statusHex("critical")} strokeWidth={2} dot={{ r:3 }} name={t("Risk Index")} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-3 gap-4 text-center text-xs">
          <div><div className="text-lg font-black text-blue-700">9,00,000</div><div className="text-[10px] text-slate-500">{t("Peak day estimate")}</div></div>
          <div><div className="text-lg font-black text-amber-600">15:00 IST</div><div className="text-[10px] text-slate-500">{t("Predicted peak hour")}</div></div>
          <div><div className="text-lg font-black text-red-600">7.8 / 10</div><div className="text-[10px] text-slate-500">{t("Peak risk index")}</div></div>
        </div>
      </ChartCard>
    </div>
  );
}
